import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { getLocationSearch } from 'Utils/uri/uriUtils';
import { appQueryParameters } from '@clubmed/utils';
import { useGQL } from 'Utils/graphql-client/use-gql/useGQL';
import { useAppContext } from 'Context/ReactAppContext';
import { getIn, useFormikContext } from 'formik';
import type { PaymentFormValues } from 'Src/types/payment';
import { formatPhoneToInternationalFormat } from 'Utils/ple-utils/phoneUtils';
import { t } from 'Src/i18n';
import { PaymentFormikInput } from 'Components/widgets/payment-info/paymentFormik';
import { VoxpayEmailInput } from './VoxpayEmailInput';
import { VoxpayPhoneInput } from './VoxpayPhoneInput';
import { useCustomerId } from './useCustomerId';
type VoxpayFormProps = {
  isDisabled?: boolean;
  isCreditCardMethod: boolean;
};
type PhoneAndEmail = {
  phone?: string;
  email?: string;
};
export const VOXPAY_PHONE = 'phone';
export const VOXPAY_SMS = 'sms';
export const VOXPAY_EMAIL = 'email';
export const VoxpayForm = ({ isDisabled, isCreditCardMethod }: VoxpayFormProps) => {
  const { query: queryParams } = useRouter();
  const location = {
    queryParams,
  };
  const { locale, customerNeolaneId } = useAppContext();
  const formik = useFormikContext<PaymentFormValues>();
  const locationSearch = getLocationSearch(location);
  const customerId = useCustomerId(customerNeolaneId);
  const { selectedVoxpayMethod } = formik.values;
  const emailFieldName = 'billingDetails.email';
  const phoneFieldName = 'billingDetails.mobilePhone';
  const isVoxpayPhoneAllowed =
    isCreditCardMethod &&
    (locationSearch.includes(appQueryParameters.cxo.call_reference) ||
      locationSearch.includes(appQueryParameters.cxo.contact_id));
  useGQL<PhoneAndEmail>({
    query: `{
          profile(userId: "${customerId}", throw401Error: true) {
            email
            phone:mobilePhoneNumber
          }
        }
        `,
    queryOptions: {
      requestName: 'PROFILE_EMAIL_PHONE',
    },
    cache: false,
    map: (result) => {
      const { email = '', phone = '' } = result.profile || {};
      formik.setFieldValue(emailFieldName, email);
      formik.setFieldValue(phoneFieldName, formatPhoneToInternationalFormat(phone, locale));
    },
  });

  useEffect(() => {
    formik.setFieldValue('selectedVoxpayMethod', VOXPAY_SMS);
  }, []);

  // When toggling between voxpay methods, the field that just became unselected
  // can still display a stale "required" error if the user had focused it while empty.
  // We don't want to wipe values the user typed, so only reset touched/error for the
  // unselected field if it is empty.
  useEffect(() => {
    if (!selectedVoxpayMethod) {
      return;
    }

    const unselectedFields: string[] = [];

    if (selectedVoxpayMethod !== VOXPAY_SMS) {
      unselectedFields.push(phoneFieldName);
    }

    if (selectedVoxpayMethod !== VOXPAY_EMAIL) {
      unselectedFields.push(emailFieldName);
    }

    unselectedFields.forEach((fieldName) => {
      const value = getIn(formik.values, fieldName);

      if (!value) {
        formik.setFieldTouched(fieldName, false, false);
        formik.setFieldError(fieldName, undefined);
      }
    });
  }, [selectedVoxpayMethod]);

  const voxpayMethodsFields = [
    {
      value: VOXPAY_SMS,
      Input: VoxpayPhoneInput,
    },
    {
      value: VOXPAY_EMAIL,
      Input: VoxpayEmailInput,
    },
    {
      value: VOXPAY_PHONE,
      isVisible: isVoxpayPhoneAllowed,
    },
  ];
  return (
    <div className="p-20">
      <div className="my-5 text-sienna">
        {t(`booking.partners.linkPayment.${selectedVoxpayMethod}.sendEmail`)}
      </div>
      <div className="flex justify-around">
        {voxpayMethodsFields.map(
          ({ isVisible = true, value, Input = null }) =>
            isVisible && (
              <div className="w-full" key={value}>
                <PaymentFormikInput
                  label={t(`booking.partners.linkPayment.${value}.title`)}
                  name="selectedVoxpayMethod"
                  value={value}
                  type="radio"
                />
                {Input && (
                  <div className="pl-30">
                    <Input
                      isDisabled={isDisabled}
                      withValidation={selectedVoxpayMethod === value}
                      fieldName={value === VOXPAY_SMS ? phoneFieldName : emailFieldName}
                    />
                  </div>
                )}
              </div>
            ),
        )}
      </div>
    </div>
  );
};
